/**
 * DREAL fine tick — pure helper extracted from coreSlice.nextDay.
 *
 * 02/06/2026 — Pre-prod code review refactor : the daily DREAL fine roll
 * was inlined inside the giant `nextDay` function (~60 lines). Extracting
 * it into a pure function reduces the orchestrator's cognitive load and
 * makes the rule directly unit-testable in isolation.
 *
 * Inputs : the pre-flip game state, the post-fatigue driver array, and
 * the working-day predicate (passed in to keep this helper free of any
 * import-time time / date concerns).
 *
 * Output (pure descriptor — caller applies side-effects atomically) :
 *   {
 *     totalFine: number,           // sum of all fines this tick (€)
 *     repHit: number,              // reputation deltas to subtract
 *     cooldownUpdates: object,     // map of `${driverId}-${trainingKey}` → nextEligibleDay
 *     memos: Array<MessagePayload>,// to push live via pushCustomMessage
 *     notifications: Array<Notification>,
 *   }
 *
 * Gated by Claire Maréchal being hired AND the calendar day being a
 * working day (no DREAL controls on weekends).
 */

import { DREAL_FINE_CONFIG, DREAL_FINE_COOLDOWN_DAYS, buildDrealFineMessage } from './drealFineBuilder';
import { getDriverTrainingStatus } from './hseCompliance';
import { generateId } from '../helpers';

/**
 * @param {Object} state - pre-flip game state (state.totalDays, state.day, state.month, state.hiredSupport, state.drealFineCooldowns)
 * @param {Array}  driversAfterLease - drivers post-fatigue / lease detach
 * @param {Function} isWorkingDay - predicate(day, month) → bool
 * @param {Function} [rng=Math.random] - injectable for tests
 * @returns {{totalFine: number, repHit: number, cooldownUpdates: object, memos: Array, notifications: Array}}
 */
export function runDrealFineTick(state, driversAfterLease, isWorkingDay, rng = Math.random) {
  const empty = { totalFine: 0, repHit: 0, cooldownUpdates: {}, memos: [], notifications: [] };
  if (!(state?.hiredSupport || {})['role-claire-marechal']) return empty;
  if (!isWorkingDay(state.day, state.month)) return empty;

  const tomorrowTotal = (state.totalDays || 0) + 1;
  const cooldowns = state.drealFineCooldowns || {};
  const cooldownUpdates = {};
  const memos = [];
  const notifications = [];
  let totalFine = 0;
  let repHit = 0;

  for (const driver of driversAfterLease) {
    if (driver.status !== 'active' || !driver.assignedVehicleId) continue;
    for (const trainingKey of Object.keys(DREAL_FINE_CONFIG)) {
      const cfg = DREAL_FINE_CONFIG[trainingKey];
      const status = getDriverTrainingStatus(driver, trainingKey, tomorrowTotal);
      if (!status.applicable || status.status !== 'expired') continue;
      const cdKey = `${driver.id}-${trainingKey}`;
      const cdUntil = (cooldownUpdates[cdKey] != null)
        ? cooldownUpdates[cdKey]
        : (cooldowns[cdKey] || 0);
      if (tomorrowTotal < cdUntil) continue;
      if (rng() >= cfg.dailyProba) continue;
      // Fine fires.
      const amount = Math.round(cfg.amountMin + rng() * (cfg.amountMax - cfg.amountMin));
      totalFine += amount;
      repHit += cfg.repHit;
      cooldownUpdates[cdKey] = tomorrowTotal + DREAL_FINE_COOLDOWN_DAYS;
      memos.push(buildDrealFineMessage({
        trainingKey,
        driverName: driver.name,
        fineAmount: amount,
        expiresOn: status.expiresOn,
        totalDays: tomorrowTotal,
      }));
      notifications.push({
        id: generateId(),
        type: 'dreal_fine',
        title: 'Amende DREAL',
        message: `${driver.name} — ${trainingKey.toUpperCase()} expirée : ${amount.toLocaleString('fr-FR')} €`,
        day: tomorrowTotal,
        read: false,
      });
    }
  }

  return { totalFine, repHit, cooldownUpdates, memos, notifications };
}
